"use client";

/**
 * AdminAccessEscape
 *
 * Aviso de saida para quem foi barrado pelo brand-group guard.
 *
 * Um app instalado como app do cliente bloqueia `/admin` e `/login` e
 * redireciona para a pagina da marca. Sem uma saida visivel, um administrador
 * que instalou ou abriu o app pela area do cliente ficava preso: cada tentativa
 * de abrir o login do estabelecimento voltava para a marca, e a unica solucao
 * era apagar os dados do site ou reinstalar o app.
 *
 * O aviso aparece somente depois de uma navegacao barrada nesta sessao, entao
 * um cliente que usa o app normalmente nunca o ve. O link faz uma navegacao
 * completa para `/login?acesso=estabelecimento`, rota sempre liberada pelo
 * guard, que tambem destrava o dispositivo.
 *
 * ADR-002, ADR-003.
 */

import { useCallback, useEffect, useState } from "react";
import { FiExternalLink, FiX } from "react-icons/fi";
import {
  ADMIN_ESCAPE_PATH,
  BRAND_GUARD_BLOCKED_EVENT,
  clearBrandGuardBlocked,
  isBrandGuardBlocked,
} from "../../lib/installProfile";

export function AdminAccessEscape() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Leitura no mount e o caminho principal: o guard grava a marcacao e
    // redireciona, entao este componente costuma montar depois do bloqueio e
    // nunca veria o evento.
    if (isBrandGuardBlocked()) setVisible(true);

    // O evento cobre o caso em que o componente ja estava montado.
    function handleBlocked() {
      setVisible(true);
    }
    window.addEventListener(BRAND_GUARD_BLOCKED_EVENT, handleBlocked);
    return () => window.removeEventListener(BRAND_GUARD_BLOCKED_EVENT, handleBlocked);
  }, []);

  const dismiss = useCallback(() => {
    clearBrandGuardBlocked();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="status"
      className="agiliza-admin-escape fixed z-[10001] rounded-xl border border-slate-200 bg-white p-3 shadow-xl shadow-slate-900/20"
    >
      <div className="flex items-start gap-3 pr-7">
        <div className="min-w-0 flex-1">
          <p className="m-0 text-sm font-bold leading-5 text-slate-950">
            Área do estabelecimento bloqueada
          </p>
          <p className="m-0 mt-1 text-xs leading-5 text-slate-600">
            Este app está instalado como app do cliente. Para entrar como estabelecimento
            neste aparelho, use o acesso abaixo.
          </p>

          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            <a
              href={ADMIN_ESCAPE_PATH}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3.5 text-sm font-bold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <FiExternalLink className="h-4 w-4" aria-hidden="true" />
              Acesso do estabelecimento
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="inline-flex h-9 items-center justify-center rounded-lg px-3 text-sm font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2"
            >
              Agora não
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2"
          aria-label="Fechar aviso de acesso do estabelecimento"
        >
          <FiX className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <style>{`
        .agiliza-admin-escape {
          bottom: calc(5.25rem + env(safe-area-inset-bottom));
          left: 0.75rem;
          right: 0.75rem;
          margin-left: auto;
          margin-right: auto;
          max-width: min(380px, calc(100vw - 1.5rem));
        }

        @media (min-width: 640px) {
          .agiliza-admin-escape {
            bottom: 1rem;
            left: auto;
            right: 1rem;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
